import {useEffect, useState} from 'react';
import styles from '../../styles/dashboard.module.css';
import Header from '../../components/header/header';
import PageTitle from '../../components/page_title/page_title';
import EmptyPlaceholder from '../../components/empty_placeholder/empty_placeholder';
import Modal from '../../components/modal/modal';
import ChipInput from '../../components/chip_input/chip_input';
import axiosInstance from '../../AxiosInstance';
import {formatTimestamp} from '../../components/utils';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
    faDownload, faTrash, faRotateRight, faPencil,
    faToggleOn, faToggleOff, faPlay
} from '@fortawesome/free-solid-svg-icons';
import {useTranslation} from 'react-i18next';
import PaginationControls, {getStoredPageSize} from '../../components/pagination/PaginationControls';

// ─── Constants ───────────────────────────────────────────────────────────────

// All possible report types (filtered dynamically by available_types endpoint)
const ALL_REPORT_TYPE_OPTIONS = [
    {
        id: 'alerts',
        nameKey: 'reports.alertsReport',
        descriptionKey: 'reports.alertsDesc',
    },
    {
        id: 'production',
        nameKey: 'reports.productionReport',
        descriptionKey: 'reports.productionDesc',
    },
    {
        id: 'predictive',
        nameKey: 'reports.maintenanceReport',
        descriptionKey: 'reports.maintenanceDesc',
    },
    {
        id: 'comprehensive',
        nameKey: 'reports.comprehensiveReport',
        descriptionKey: 'reports.comprehensiveDesc',
    },
];

const PERIOD_OPTIONS = [
    {id: 'daily', labelKey: 'reports.daily'},
    {id: 'weekly', labelKey: 'reports.weekly'},
    {id: 'monthly', labelKey: 'reports.monthly'},
];

const DOW_OPTIONS = [
    {id: 0, labelKey: 'reports.days.monday'}, {id: 1, labelKey: 'reports.days.tuesday'}, {id: 2, labelKey: 'reports.days.wednesday'},
    {id: 3, labelKey: 'reports.days.thursday'}, {id: 4, labelKey: 'reports.days.friday'}, {id: 5, labelKey: 'reports.days.saturday'},
    {id: 6, labelKey: 'reports.days.sunday'},
];

const STATUS_KEYS = {
    pending:    {labelKey: 'reports.status.pending',    color: '#f59e0b'},
    generating: {labelKey: 'reports.status.generating', color: '#3949ab'},
    ready:      {labelKey: 'reports.status.ready',      color: '#22c55e'},
    failed:     {labelKey: 'reports.status.failed',     color: '#ef4444'},
};


const yesterday = new Date();
yesterday.setDate(yesterday.getDate() - 1);
const YESTERDAY_STR = yesterday.toISOString().split('T')[0];

const EMPTY_GENERATE_FORM = {
    report_type: 'alerts',
    period: 'daily',
    period_date: YESTERDAY_STR,
};

/** Computes the actual date range that the backend will use for a given period + anchor date. */
function computePeriodRange(period, dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    if (period === 'daily') {
        return dateStr;
    } else if (period === 'weekly') {
        const dow = d.getDay(); // 0=Sun
        const diffToMon = dow === 0 ? -6 : 1 - dow;
        const mon = new Date(d); mon.setDate(d.getDate() + diffToMon);
        const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
        return `${mon.toISOString().split('T')[0]} → ${sun.toISOString().split('T')[0]}`;
    } else if (period === 'monthly') {
        const firstDay = new Date(d.getFullYear(), d.getMonth(), 1);
        const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0);
        return `${firstDay.toISOString().split('T')[0]} → ${lastDay.toISOString().split('T')[0]}`;
    }
    return '';
}

const EMPTY_SCHEDULE_FORM = {
    name: '',
    frequency: 'weekly',
    send_time: '08:00',
    day_of_week: 0,
    day_of_month: 1,
    report_types: [],
    email_recipients: [],
    is_active: true,
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const thStyle = {
    padding: '10px 14px', textAlign: 'center',
    fontSize: '12px', fontWeight: 600, color: '#64748B',
};

const tdStyle = {
    padding: '10px 14px', fontSize: '13px', color: '#334155', textAlign: 'center',
};

const inputStyle = {
    padding: '6px 10px', borderRadius: '6px', border: '1px solid #D1D5DB',
    fontSize: '13px', color: '#1F2937', width: '100%', outline: 'none',
    backgroundColor: '#FFFFFF',
};

const labelStyle = {
    fontSize: '13px', fontWeight: 500, color: '#374151',
    display: 'block', marginBottom: '4px', marginTop: '12px',
};

const tabStyle = (active) => ({
    padding: '8px 20px', borderRadius: '6px', cursor: 'pointer',
    fontSize: '13px', fontWeight: 600, border: 'none',
    backgroundColor: active ? '#4F46E5' : '#F1F5F9',
    color: active ? '#FFFFFF' : '#64748B',
    transition: 'all 0.15s',
});

const actionBtnStyle = (color) => ({
    background: 'none', border: 'none', cursor: 'pointer',
    color: color, padding: '4px 6px', fontSize: '14px',
});

// ─── Component ───────────────────────────────────────────────────────────────

export default function Reports() {
    const {t} = useTranslation();
    const getReportTypeLabel = (type) => {
        const opt = ALL_REPORT_TYPE_OPTIONS.find(o => o.id === type);
        return opt ? t(opt.nameKey) : type;
    };
    const [activeTab, setActiveTab] = useState('reports');

    // Available report types for this org (fetched from backend)
    const [availableTypes, setAvailableTypes] = useState(['alerts']);
    const [loadingTypes, setLoadingTypes] = useState(true);

    // Reports archive state
    const [reports, setReports] = useState([]);
    const [loadingReports, setLoadingReports] = useState(true);
    const [reportsPage, setReportsPage] = useState(1);
    const [reportsPageSize, setReportsPageSize] = useState(getStoredPageSize);

    // Schedules state
    const [schedules, setSchedules] = useState([]);
    const [loadingSchedules, setLoadingSchedules] = useState(true);
    const [schedulesPage, setSchedulesPage] = useState(1);
    const [schedulesPageSize, setSchedulesPageSize] = useState(getStoredPageSize);

    // Generate modal
    const [showGenerateModal, setShowGenerateModal] = useState(false);
    const [generateForm, setGenerateForm] = useState({...EMPTY_GENERATE_FORM});

    // Schedule modal
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [editingSchedule, setEditingSchedule] = useState(null);
    const [scheduleForm, setScheduleForm] = useState({...EMPTY_SCHEDULE_FORM});

    // Delete confirmation
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [deletingType, setDeletingType] = useState(null);

    // Run Now info modal
    const [showRunNowModal, setShowRunNowModal] = useState(false);

    // ─── Data fetching ────────────────────────────────────────────────────────

    const fetchAvailableTypes = () => {
        axiosInstance.get('report/available_types/').then(res => {
            setAvailableTypes(res.data.available_types || ['alerts']);
            // Set default generate form type to first available
            setGenerateForm(f => ({
                ...f,
                report_type: (res.data.available_types || ['alerts'])[0],
            }));
            setLoadingTypes(false);
        }).catch(() => {
            setAvailableTypes(['alerts']);
            setLoadingTypes(false);
        });
    };

    const fetchReports = () => {
        axiosInstance.get('report/').then(res => {
            setReports(Array.isArray(res.data) ? res.data : (res.data.results || []));
            setLoadingReports(false);
        }).catch(() => {
            setReports([]);
            setLoadingReports(false);
        });
    };

    const fetchSchedules = () => {
        axiosInstance.get('report_schedule/').then(res => {
            setSchedules(Array.isArray(res.data) ? res.data : (res.data.results || []));
            setLoadingSchedules(false);
        }).catch(() => {
            setSchedules([]);
            setLoadingSchedules(false);
        });
    };

    useEffect(() => {
        fetchAvailableTypes();
        fetchReports();
        fetchSchedules();
    }, []);

    // Poll every 5 seconds while there are pending/generating reports
    useEffect(() => {
        const hasPending = reports.some(r => r.status === 'pending' || r.status === 'generating');
        if (!hasPending) return;
        const interval = setInterval(fetchReports, 5000);
        return () => clearInterval(interval);
    }, [reports]);

    // ─── Report actions ──────────────────────────────────────────────────────

    const handleGenerate = () => {
        axiosInstance.post('report/', generateForm).then(() => {
            setShowGenerateModal(false);
            setGenerateForm({...EMPTY_GENERATE_FORM});
            setReportsPage(1);
            fetchReports();
        });
    };

    const handleDownload = (report) => {
        axiosInstance.get(`report/${report.id}/download/`, {responseType: 'blob'}).then(res => {
            const url = URL.createObjectURL(res.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = `report_${report.report_type}_${report.period}_${report.period_date}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }).catch(() => {
            alert(t('reports.downloadFailed'));
        });
    };

    const handleRetry = (reportId) => {
        axiosInstance.post(`report/${reportId}/retry/`).then(fetchReports);
    };

    const handleDeleteReport = (id) => {
        setDeletingId(id);
        setDeletingType('report');
        setShowDeleteModal(true);
    };

    // ─── Schedule actions ────────────────────────────────────────────────────

    const openCreateSchedule = () => {
        setEditingSchedule(null);
        setScheduleForm({...EMPTY_SCHEDULE_FORM});
        setShowScheduleModal(true);
    };

    const openEditSchedule = (schedule) => {
        setEditingSchedule(schedule);
        setScheduleForm({
            name: schedule.name,
            frequency: schedule.frequency,
            send_time: schedule.send_time,
            day_of_week: schedule.day_of_week ?? 0,
            day_of_month: schedule.day_of_month ?? 1,
            report_types: schedule.report_types || [],
            email_recipients: schedule.email_recipients || [],
            is_active: schedule.is_active,
        });
        setShowScheduleModal(true);
    };

    const handleSaveSchedule = () => {
        const payload = {...scheduleForm};
        if (editingSchedule) {
            axiosInstance.put(`report_schedule/${editingSchedule.id}/`, payload).then(() => {
                setShowScheduleModal(false);
                fetchSchedules();
            });
        } else {
            axiosInstance.post('report_schedule/', payload).then(() => {
                setShowScheduleModal(false);
                fetchSchedules();
            });
        }
    };

    const handleDeleteSchedule = (id) => {
        setDeletingId(id);
        setDeletingType('schedule');
        setShowDeleteModal(true);
    };

    const handleToggleActive = (schedule) => {
        axiosInstance.put(`report_schedule/${schedule.id}/`, {is_active: !schedule.is_active})
            .then(fetchSchedules);
    };

    const handleRunNow = (id) => {
        axiosInstance.post(`report_schedule/${id}/run_now/`).then(() => {
            setShowRunNowModal(true);
            fetchReports();
        });
    };

    const confirmDelete = () => {
        if (deletingType === 'report') {
            axiosInstance.delete(`report/${deletingId}/`).then(() => {
                setShowDeleteModal(false);
                setReportsPage(1);
                fetchReports();
            });
        } else {
            axiosInstance.delete(`report_schedule/${deletingId}/`).then(() => {
                setShowDeleteModal(false);
                setSchedulesPage(1);
                fetchSchedules();
            });
        }
    };

    const toggleReportType = (type) => {
        const current = scheduleForm.report_types;
        const updated = current.includes(type)
            ? current.filter(t => t !== type)
            : [...current, type];
        setScheduleForm({...scheduleForm, report_types: updated});
    };

    // ─── Pagination helpers ───────────────────────────────────────────────────

    const handleReportsPageSizeChange = (newSize) => { setReportsPageSize(newSize); setReportsPage(1); };
    const handleSchedulesPageSizeChange = (newSize) => { setSchedulesPageSize(newSize); setSchedulesPage(1); };

    const reportsTotalPages = Math.ceil(reports.length / reportsPageSize);
    const pagedReports = reports.slice((reportsPage - 1) * reportsPageSize, reportsPage * reportsPageSize);

    const schedulesTotalPages = Math.ceil(schedules.length / schedulesPageSize);
    const pagedSchedules = schedules.slice((schedulesPage - 1) * schedulesPageSize, schedulesPage * schedulesPageSize);

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <div className={styles.dashboard_container}>
            <Header sidebarIndex={10}/>

            <div className={'mx-5 mb-4'}>
                <PageTitle title={t('reports.title')}/>

                {/* Tab Bar */}
                <div style={{display: 'flex', gap: '8px', marginBottom: '20px', marginTop: '8px'}}>
                    <button style={tabStyle(activeTab === 'reports')} onClick={() => setActiveTab('reports')}>
                        {t('reports.archiveTab')}
                    </button>
                    <button style={tabStyle(activeTab === 'schedules')} onClick={() => setActiveTab('schedules')}>
                        {t('reports.schedulesTab')}
                    </button>
                </div>

                {/* ── REPORTS ARCHIVE TAB ── */}
                {activeTab === 'reports' && (
                    <div>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px'}}>
                            <p style={{margin: 0, fontSize: '13px', color: '#64748B'}}>
                                {t('reports.reportsCount', {count: reports.length})}
                            </p>
                            <button
                                onClick={() => { setGenerateForm({...EMPTY_GENERATE_FORM}); setShowGenerateModal(true); }}
                                style={{
                                    padding: '7px 16px', borderRadius: '6px',
                                    backgroundColor: '#4F46E5', color: '#FFF',
                                    border: 'none', fontSize: '13px', cursor: 'pointer', fontWeight: 500,
                                }}
                            >
                                {t('reports.newReport')}
                            </button>
                        </div>

                        {loadingReports ? (
                            <div style={{textAlign: 'center', padding: '40px', color: '#94A3B8'}}>{t('common.loading')}</div>
                        ) : reports.length > 0 ? (
                            <>
                                <div style={{overflowX: 'auto'}}>
                                <table style={{
                                    width: '100%', borderCollapse: 'collapse',
                                    backgroundColor: '#FFF', borderRadius: '8px',
                                    overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                }}>
                                    <thead>
                                        <tr style={{backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0'}}>
                                            <th style={thStyle}>{t('reports.colType')}</th>
                                            <th style={thStyle}>{t('reports.colPeriod')}</th>
                                            <th style={thStyle}>{t('reports.colDate')}</th>
                                            <th style={thStyle}>{t('reports.colCreatedBy')}</th>
                                            <th style={thStyle}>{t('reports.colGenerated')}</th>
                                            <th style={thStyle}>{t('common.status')}</th>
                                            <th style={thStyle}>{t('common.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pagedReports.map((report, i) => {
                                            const metaKey = STATUS_KEYS[report.status] || {labelKey: null, color: '#64748B'};
                                            return (
                                                <tr key={report.id} style={{borderBottom: '1px solid #F1F5F9', backgroundColor: i % 2 === 0 ? '#FFF' : '#FAFAFA'}}>
                                                    <td style={tdStyle}>{report.report_type_display}</td>
                                                    <td style={tdStyle}>{report.period_display}</td>
                                                    <td style={tdStyle}>{report.period_date}</td>
                                                    <td style={tdStyle}>{report.created_by_username || t('reports.scheduledBy')}</td>
                                                    <td style={{...tdStyle, color: '#94A3B8'}}>{formatTimestamp(report.created_at)}</td>
                                                    <td style={tdStyle}>
                                                        <span style={{color: metaKey.color, fontWeight: 600, fontSize: '12px'}}>
                                                            {metaKey.labelKey ? t(metaKey.labelKey) : report.status}
                                                        </span>
                                                    </td>
                                                    <td style={tdStyle}>
                                                        <div style={{display: 'flex', justifyContent: 'center', gap: '4px'}}>
                                                            {report.status === 'ready' && report.download_url && (
                                                                <button
                                                                    title={t('reports.download')}
                                                                    style={actionBtnStyle('#4F46E5')}
                                                                    onClick={() => handleDownload(report)}
                                                                >
                                                                    <FontAwesomeIcon icon={faDownload}/>
                                                                </button>
                                                            )}
                                                            {report.status === 'failed' && (
                                                                <button
                                                                    title={t('reports.regenerate')}
                                                                    style={actionBtnStyle('#f59e0b')}
                                                                    onClick={() => handleRetry(report.id)}
                                                                >
                                                                    <FontAwesomeIcon icon={faRotateRight}/>
                                                                </button>
                                                            )}
                                                            <button
                                                                title={t('common.delete')}
                                                                style={actionBtnStyle('#ef4444')}
                                                                onClick={() => handleDeleteReport(report.id)}
                                                            >
                                                                <FontAwesomeIcon icon={faTrash}/>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            <PaginationControls
                                page={reportsPage}
                                totalPages={reportsTotalPages}
                                pageSize={reportsPageSize}
                                onPageChange={setReportsPage}
                                onPageSizeChange={handleReportsPageSizeChange}
                                totalCount={reports.length}
                            />
                        </>
                        ) : (
                            <EmptyPlaceholder message={t('reports.noReports')}/>
                        )}
                    </div>
                )}

                {/* ── SCHEDULES TAB ── */}
                {activeTab === 'schedules' && (
                    <div>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px'}}>
                            <p style={{margin: 0, fontSize: '13px', color: '#64748B'}}>
                                {t('reports.schedulesCount', {count: schedules.length})}
                            </p>
                            <button
                                onClick={openCreateSchedule}
                                style={{
                                    padding: '7px 16px', borderRadius: '6px',
                                    backgroundColor: '#4F46E5', color: '#FFF',
                                    border: 'none', fontSize: '13px', cursor: 'pointer', fontWeight: 500,
                                }}
                            >
                                {t('reports.newSchedule')}
                            </button>
                        </div>

                        {loadingSchedules ? (
                            <div style={{textAlign: 'center', padding: '40px', color: '#94A3B8'}}>{t('common.loading')}</div>
                        ) : schedules.length > 0 ? (
                            <>
                                <div style={{overflowX: 'auto'}}>
                                <table style={{
                                    width: '100%', borderCollapse: 'collapse',
                                    backgroundColor: '#FFF', borderRadius: '8px',
                                    overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                }}>
                                    <thead>
                                        <tr style={{backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0'}}>
                                            <th style={thStyle}>{t('common.name')}</th>
                                            <th style={thStyle}>{t('reports.colFrequency')}</th>
                                            <th style={thStyle}>{t('reports.colTime')}</th>
                                            <th style={thStyle}>{t('reports.colReportTypes')}</th>
                                            <th style={thStyle}>{t('reports.colRecipients')}</th>
                                            <th style={thStyle}>{t('common.status')}</th>
                                            <th style={thStyle}>{t('common.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pagedSchedules.map((sch, i) => (
                                            <tr key={sch.id} style={{borderBottom: '1px solid #F1F5F9', backgroundColor: i % 2 === 0 ? '#FFF' : '#FAFAFA'}}>
                                                <td style={{...tdStyle, fontWeight: 500}}>{sch.name}</td>
                                                <td style={tdStyle} className="text-capitalize">{sch.frequency}</td>
                                                <td style={tdStyle}>{sch.send_time}</td>
                                                <td style={tdStyle}>
                                                    <div style={{display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'center'}}>
                                                        {(sch.report_types || []).map(type => (
                                                            <span key={type} style={{
                                                                backgroundColor: '#EEF2FF', color: '#3730A3',
                                                                borderRadius: '4px', padding: '1px 6px',
                                                                fontSize: '11px', fontWeight: 500,
                                                            }}>
                                                                {getReportTypeLabel(type)}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td style={tdStyle}>{t('reports.emailsCount', {count: (sch.email_recipients || []).length})}</td>
                                                <td style={tdStyle}>
                                                    <span style={{
                                                        color: sch.is_active ? '#22c55e' : '#94A3B8',
                                                        fontWeight: 600, fontSize: '12px',
                                                    }}>
                                                        {sch.is_active ? t('common.active') : t('common.inactive')}
                                                    </span>
                                                </td>
                                                <td style={tdStyle}>
                                                    <div style={{display: 'flex', justifyContent: 'center', gap: '4px'}}>
                                                        <button title={t('reports.runNow')} style={actionBtnStyle('#22c55e')} onClick={() => handleRunNow(sch.id)}>
                                                            <FontAwesomeIcon icon={faPlay}/>
                                                        </button>
                                                        <button title={t('common.edit')} style={actionBtnStyle('#4F46E5')} onClick={() => openEditSchedule(sch)}>
                                                            <FontAwesomeIcon icon={faPencil}/>
                                                        </button>
                                                        <button title={sch.is_active ? t('reports.disable') : t('reports.enable')} style={actionBtnStyle(sch.is_active ? '#f59e0b' : '#22c55e')} onClick={() => handleToggleActive(sch)}>
                                                            <FontAwesomeIcon icon={sch.is_active ? faToggleOn : faToggleOff}/>
                                                        </button>
                                                        <button title={t('common.delete')} style={actionBtnStyle('#ef4444')} onClick={() => handleDeleteSchedule(sch.id)}>
                                                            <FontAwesomeIcon icon={faTrash}/>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <PaginationControls
                                page={schedulesPage}
                                totalPages={schedulesTotalPages}
                                pageSize={schedulesPageSize}
                                onPageChange={setSchedulesPage}
                                onPageSizeChange={handleSchedulesPageSizeChange}
                                totalCount={schedules.length}
                            />
                            </>
                        ) : (
                            <EmptyPlaceholder message={t('reports.noSchedules')}/>
                        )}
                    </div>
                )}
            </div>

            {/* ── GENERATE REPORT MODAL ── */}
            <Modal
                show={showGenerateModal}
                setter={setShowGenerateModal}
                name={t('reports.generateReport')}
                buttonName={t('reports.generateReport')}
                onClick={handleGenerate}
            >
                <div>
                    <label style={labelStyle}>{t('reports.reportType')}</label>
                    {loadingTypes ? (
                        <p style={{fontSize: '12px', color: '#94A3B8'}}>{t('reports.loadingTypes')}</p>
                    ) : (
                        <div style={{display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '4px'}}>
                            {ALL_REPORT_TYPE_OPTIONS.filter(o => availableTypes.includes(o.id)).map(o => (
                                <label key={o.id} style={{
                                    display: 'flex', alignItems: 'flex-start', gap: '10px',
                                    cursor: 'pointer', padding: '10px 12px', borderRadius: '8px',
                                    border: generateForm.report_type === o.id ? '2px solid #4F46E5' : '1px solid #E2E8F0',
                                    backgroundColor: generateForm.report_type === o.id ? '#EEF2FF' : '#FAFAFA',
                                    transition: 'all 0.1s',
                                }}>
                                    <input
                                        type="radio"
                                        name="report_type"
                                        value={o.id}
                                        checked={generateForm.report_type === o.id}
                                        onChange={() => setGenerateForm({...generateForm, report_type: o.id})}
                                        style={{marginTop: '2px', accentColor: '#4F46E5'}}
                                    />
                                    <div>
                                        <div style={{fontSize: '13px', fontWeight: 600, color: '#1F2937'}}>{t(o.nameKey)}</div>
                                        <div style={{fontSize: '11px', color: '#64748B', marginTop: '2px'}}>{t(o.descriptionKey)}</div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    )}

                    <label style={labelStyle}>{t('reports.period')}</label>
                    <select
                        style={inputStyle}
                        value={generateForm.period}
                        onChange={e => setGenerateForm({...generateForm, period: e.target.value})}
                    >
                        {PERIOD_OPTIONS.map(o => <option key={o.id} value={o.id}>{t(o.labelKey)}</option>)}
                    </select>

                    <label style={labelStyle}>{t('reports.periodDate')}</label>
                    <input
                        type="date"
                        style={inputStyle}
                        value={generateForm.period_date}
                        onChange={e => setGenerateForm({...generateForm, period_date: e.target.value})}
                    />
                    {generateForm.period_date && (
                        <p style={{fontSize: '11px', color: '#4F46E5', marginTop: '4px', fontWeight: 500}}>
                            {t('reports.dataRange')} {computePeriodRange(generateForm.period, generateForm.period_date)}
                        </p>
                    )}
                </div>
            </Modal>

            {/* ── SCHEDULE MODAL ── */}
            <Modal
                show={showScheduleModal}
                setter={setShowScheduleModal}
                name={editingSchedule ? t('common.edit') : t('reports.createSchedule')}
                buttonName={editingSchedule ? t('common.save') : t('reports.createSchedule')}
                onClick={handleSaveSchedule}
            >
                <div>
                    <label style={labelStyle}>{t('reports.scheduleName')}</label>
                    <input
                        style={inputStyle}
                        type="text"
                        placeholder="e.g. Weekly Operations Report"
                        value={scheduleForm.name}
                        onChange={e => setScheduleForm({...scheduleForm, name: e.target.value})}
                    />

                    <label style={labelStyle}>{t('reports.frequency')}</label>
                    <select
                        style={inputStyle}
                        value={scheduleForm.frequency}
                        onChange={e => setScheduleForm({...scheduleForm, frequency: e.target.value})}
                    >
                        {PERIOD_OPTIONS.map(o => <option key={o.id} value={o.id}>{t(o.labelKey)}</option>)}
                    </select>

                    <label style={labelStyle}>{t('reports.sendTime')}</label>
                    <input
                        type="time"
                        style={inputStyle}
                        value={scheduleForm.send_time}
                        onChange={e => setScheduleForm({...scheduleForm, send_time: e.target.value})}
                    />

                    {scheduleForm.frequency === 'weekly' && (
                        <>
                            <label style={labelStyle}>{t('reports.dayOfWeek')}</label>
                            <select
                                style={inputStyle}
                                value={scheduleForm.day_of_week}
                                onChange={e => setScheduleForm({...scheduleForm, day_of_week: parseInt(e.target.value)})}
                            >
                                {DOW_OPTIONS.map(o => <option key={o.id} value={o.id}>{t(o.labelKey)}</option>)}
                            </select>
                        </>
                    )}

                    {scheduleForm.frequency === 'monthly' && (
                        <>
                            <label style={labelStyle}>{t('reports.dayOfMonth')}</label>
                            <input
                                type="number"
                                min="1"
                                max="28"
                                style={inputStyle}
                                value={scheduleForm.day_of_month}
                                onChange={e => setScheduleForm({...scheduleForm, day_of_month: parseInt(e.target.value)})}
                            />
                        </>
                    )}

                    <label style={labelStyle}>{t('reports.reportTypesToInclude')}</label>
                    <div style={{display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '4px'}}>
                        {ALL_REPORT_TYPE_OPTIONS.filter(rt => availableTypes.includes(rt.id)).map(rt => (
                            <label key={rt.id} style={{display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#374151', fontWeight: 400}}>
                                <input
                                    type="checkbox"
                                    checked={scheduleForm.report_types.includes(rt.id)}
                                    onChange={() => toggleReportType(rt.id)}
                                    style={{marginTop: '2px'}}
                                />
                                <div>
                                    <div style={{fontWeight: 500}}>{t(rt.nameKey)}</div>
                                    <div style={{fontSize: '11px', color: '#94A3B8'}}>{t(rt.descriptionKey)}</div>
                                </div>
                            </label>
                        ))}
                    </div>

                    <label style={labelStyle}>{t('reports.emailRecipients')}</label>
                    <ChipInput
                        value={scheduleForm.email_recipients}
                        onChange={(chips) => setScheduleForm({...scheduleForm, email_recipients: chips})}
                        placeholder={t('reports.emailRecipients')}
                    />

                    <label style={{...labelStyle, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer'}}>
                        <input
                            type="checkbox"
                            checked={scheduleForm.is_active}
                            onChange={e => setScheduleForm({...scheduleForm, is_active: e.target.checked})}
                        />
                        {t('reports.activeLabel')}
                    </label>
                </div>
            </Modal>

            {/* ── RUN NOW INFO MODAL ── */}
            <Modal
                show={showRunNowModal}
                setter={setShowRunNowModal}
                name={t('reports.runNowTitle')}
                buttonName={t('reports.runNowGotIt')}
                onClick={() => setShowRunNowModal(false)}
            >
                <div style={{textAlign: 'center', padding: '8px 0'}}>
                    <div style={{fontSize: '36px', marginBottom: '12px'}}>📬</div>
                    <p style={{fontSize: '14px', color: '#374151', marginBottom: '8px', fontWeight: 500}}>
                        {t('reports.runNowMessage')}
                    </p>
                    <p style={{fontSize: '13px', color: '#64748B'}}>
                        {t('reports.runNowDetail')}
                    </p>
                </div>
            </Modal>

            {/* ── DELETE CONFIRMATION MODAL ── */}
            <Modal
                show={showDeleteModal}
                setter={setShowDeleteModal}
                name={t('reports.confirmDeleteTitle')}
                buttonName={t('common.delete')}
                onClick={confirmDelete}
                danger
            >
                <p style={{fontSize: '14px', color: '#374151'}}>
                    {deletingType === 'report' ? t('reports.deleteReportConfirm') : t('reports.deleteScheduleConfirm')}
                </p>
            </Modal>
        </div>
    );
}
